import * as yup from 'yup'

document.addEventListener('DOMContentLoaded', () => {
  const form = document.querySelector('#contact-form')
  if (!form) return

  const submitButton = form.querySelector('button[type="submit"]')
  const response = document.getElementById('form-response')
  const buttonTextSpan = submitButton ? submitButton.querySelector('span') : null

  if (!submitButton || !buttonTextSpan) {
    console.error('Submit button or its <span> not found.')
    return
  }

  const originalButtonText = buttonTextSpan.textContent
  const formControls = form.querySelectorAll('input, textarea, select, button')

  const fields = {
    name: form.querySelector('input[name="name"]'),
    email: form.querySelector('input[name="email"]'),
    message: form.querySelector('textarea[name="message"]')
  }

  const errorEls = {
    name: document.getElementById('name-error'),
    email: document.getElementById('email-error'),
    message: document.getElementById('message-error')
  }

  Object.values(errorEls).forEach(el => {
    if (el) el.hidden = true
  })
  if (response) {
    response.classList.remove('is-shown')
    response.style.opacity = ''
  }

  const schema = yup.object({
    name: yup.string().required('Please enter your name').min(1).max(50),
    email: yup.string().required('Please enter email address').min(1).max(50).email(),
    message: yup.string().required('Please enter message').min(10).max(500)
  })

  function setFieldError(fieldName, message) {
    const input = fields[fieldName]
    const errorEl = errorEls[fieldName]
    if (!input || !errorEl) return

    input.setAttribute('aria-invalid', message ? 'true' : 'false')
    input.setAttribute('aria-describedby', message ? errorEl.id : '')
    errorEl.textContent = message || ''
    errorEl.hidden = !message
  }

  function clearAllErrors() {
    Object.keys(fields).forEach((name) => setFieldError(name, ''))
  }

  function setFormState(isSending) {
    if (isSending) {
      buttonTextSpan.textContent = 'Submitting...'
      formControls.forEach(control => {
        control.disabled = true
      })
      return
    }

    form.reset()
    formControls.forEach(control => {
      control.disabled = false
      if (control.hasAttribute('aria-invalid')) {
        control.setAttribute('aria-invalid', 'false')
      }
      if (control.hasAttribute('aria-describedby')) {
        control.setAttribute('aria-describedby', '')
      }
    })
    clearAllErrors()
    buttonTextSpan.textContent = originalButtonText
  }

  function showResponse(message = '', error = false) {
    if (!response) return
    response.innerText = message
    if (error) {
      response.classList.add('text-error')
    }
    response.classList.add('is-shown')

    setTimeout(() => {
      response.style.opacity = 1
    }, 10)

    setTimeout(() => {
      response.style.opacity = 0
      setTimeout(() => {
        response.innerText = ''
        response.classList.remove('is-shown')
        response.classList.remove('text-error')
        response.style.opacity = ''
      }, 300)
    }, 5000)
  }

  function getFormData() {
    return {
      name: (fields.name?.value || '').trim(),
      email: (fields.email?.value || '').trim(),
      message: (fields.message?.value || '').trim()
    }
  }

  async function validateClient() {
    clearAllErrors()
    const values = getFormData()

    try {
      await schema.validate(values, { abortEarly: false })
      return { ok: true, values }
    } catch (err) {
      // yup ValidationError
      const map = {}
      if (err && err.inner && Array.isArray(err.inner)) {
        err.inner.forEach((e) => {
          if (e.path && !map[e.path]) map[e.path] = e.message
        })
      } else if (err?.path) {
        map[err.path] = err.message
      }

      // Apply errors to UI
      Object.keys(map).forEach((field) => setFieldError(field, map[field]))
      return { ok: false, errors: map }
    }
  }

  async function getRecaptchaToken() {
    const siteKey = window.contactForm?.recaptchaSiteKey
    const action = window.contactForm?.recaptchaAction || 'contact_form'

    if (!siteKey) {
      console.warn('[Contact Form] reCAPTCHA site key is not configured')
      return ''
    }

    await new Promise((resolve, reject) => {
      if (!window.grecaptcha || !window.grecaptcha.ready) {
        reject(new Error('[Contact Form] reCAPTCHA is not loaded'))
        return
      }
      window.grecaptcha.ready(resolve)
    })

    return window.grecaptcha.execute(siteKey, { action })
  }


  async function submitAjax(values) {
    const fd = new FormData()
    fd.append('action', 'contact_form_submit')

    const nonce = window.contactForm?.nonce || ''
    fd.append('nonce', nonce)

    fd.append('name', values.name)
    fd.append('email', values.email)
    fd.append('message', values.message)

    if (!window.contactForm?.recaptchaEnabled) {
      console.warn('[Contact Form] reCAPTCHA is disabled')
    } else {
      try {
        const token = await getRecaptchaToken()
        if (!token) {
          throw new Error('reCAPTCHA verification failed. Please try again.')
        }
        fd.append('recaptcha_token', token)
      } catch (e) {
        throw e
      }
    }

    const ajaxUrl = window.contactForm?.ajaxUrl || '/wp-admin/admin-ajax.php'

    const res = await fetch(ajaxUrl, {
      method: 'POST',
      credentials: 'same-origin',
      body: fd,
      headers: { 'Accept': 'application/json' }
    })

    const json = await res.json().catch(() => null)

    if (!json) {
      throw new Error('Bad server response.')
    }

    if (!json.success) {
      const status = res.status || 400
      const data = json.data || {}

      // Field errors from server: { errors: { name: "...", email: "..." } }
      if (data.errors && typeof data.errors === 'object') {
        Object.keys(data.errors).forEach((field) => {
          setFieldError(field, data.errors[field])
        })
      }

      const msg = data.message || 'Request failed.'
      const err = new Error(msg)
      err.status = status
      throw err
    }

    return json.data
  }

  // Validate on blur
  Object.keys(fields).forEach((key) => {
    const el = fields[key]
    if (!el) return
    el.addEventListener('blur', async () => {
      const values = getFormData()
      try {
        await schema.validateAt(key, values)
        setFieldError(key, '')
      } catch (e) {
        setFieldError(key, e.message || 'Invalid value')
      }
    })
  })

  form.addEventListener('submit', async (event) => {
    event.preventDefault()

    const v = await validateClient()
    if (!v.ok) return

    setFormState(true)

    try {
      const res = await submitAjax(v.values)
      setFormState(false)
      showResponse(res.clientMessage)
    } catch (e) {
      formControls.forEach(control => {
        control.disabled = false
      })
      showResponse(e.clientMessage || 'An unexpected error occurred during submission. Please try again in a few moments or contact us directly if the issue persists.', true)
      buttonTextSpan.textContent = originalButtonText
      console.error('[Contact Form] ' + e)
    }
  })
})
